import { UserDTOInternalRolesEnum } from '@reposit/api-client';
import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-grid-system';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { getBreakpoint } from '../../base/style';
import Button from '../../components/Button';
import { FullPageLoader } from '../../components/Loading';
import RepositCard from '../../components/Reposit/RepositCard';
import { Header4, P2 } from '../../components/Typography';
import { Option } from '../../interfaces/option.interface';
import { hasInternalRole } from '../../redux/account/account.selectors';
import { createLoadingSelector } from '../../redux/loading/loading.selector';
import {
  fetchOrganizationUsersWithRolesRequested,
  FETCH_ORGANIZATION_USERS_WITH_ROLES_STORE_KEY,
} from '../../redux/organization/organization.actions';
import { getOrganizationUsersWithRoles } from '../../redux/organization/organization.selectors';
import { AppState } from '../../redux/root.reducer';
import { fetchOrganizationUsersRequested, FETCH_ORGANIZATION_USERS_STORE_KEY } from '../../redux/user/user.actions';
import { getOrganizationUsers } from '../../redux/user/user.selectors';
import { GrantRolesModal } from './grant-roles-modal';
import { NotifyModal } from './notify-modal';
import { RevokeRolesModal } from './revoke-roles-modal';

const TeamListWrapper = styled.ul`
  background: #fff;
  margin: 22px 0 36px;
  min-width: 200px;
  overflow: hidden;
  padding: 0;
`;

const TeamMemberItem = styled.div<{ isOpen?: boolean }>`
  background: ${(props) => props.theme.colors.bgAccent};
  margin: 0 0 6px;
  padding: 0 24px 0;

  @media all and (min-width: ${getBreakpoint('lg')}) {
    padding: ${(props) => (props.isOpen ? `34px` : `5px 34px`)};
  }

  &:hover {
    background: ${(props) => (!props.isOpen ? `#f2f3f4` : props.theme.colors.bgAccent)};
  }
`;

const TeamMemberItemInner = styled.li`
  border-radius: 8px;
  cursor: default;
  box-sizing: border-box;
  padding: 12px 0;
  height: 96px;

  @media all and (min-width: ${getBreakpoint('md')}) {
    height: 112px;
  }

  @media all and (min-width: ${getBreakpoint('lg')}) {
    align-items: center;
    display: flex;
    justify-content: space-between;
    height: 48px;
    padding: 0;
  }
`;

const TeamMember = styled.div`
  margin-bottom: 6px;
  @media all and (min-width: ${getBreakpoint('lg')}) {
    margin-bottom: 0;
  }
`;

const Name = styled(Header4)`
  font-size: 1em;
  margin: 0;

  @media all and (min-width: ${getBreakpoint('lg')}) {
    margin: 0 10px 0 0;
  }
`;

const Email = styled(P2)`
  margin: 0;
`;

const ViewLink = styled(NavLink)`
  margin: 0 0 0 auto;
`;

const JobTitle = styled.span`
  font-weight: normal;
`;

export const RolePill = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 9999px;
  background: ${(props) => props.theme.colors.warning};
  color: #ffffff;
  font-size: 0.75em;
  font-weight: normal;
  text-transform: uppercase;
  font-family: ${(props) => props.theme.typography.face.primary};
  line-height: 1.4;
`;

export const NoAdminPill = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 3px 10px;
  border-radius: 9999px;
  background: ${(props) => props.theme.colors.negative};
  color: #ffffff;
  font-size: 0.75em;
  font-weight: normal;
  text-transform: uppercase;
  font-family: ${(props) => props.theme.typography.face.primary};
  line-height: 1.4;
`;

interface OrganizationUsersProps {
  organizationId: string;
  integratorOptions: Option[];
}

const OrganizationUsers: React.FC<OrganizationUsersProps> = ({ organizationId, integratorOptions }) => {
  const dispatch = useDispatch();
  const users = useSelector(getOrganizationUsers);
  const isFetchingLoadingSelector = createLoadingSelector([
    FETCH_ORGANIZATION_USERS_STORE_KEY,
    FETCH_ORGANIZATION_USERS_WITH_ROLES_STORE_KEY,
  ]);
  const isOrgLoading = useSelector(isFetchingLoadingSelector);
  const [userId, setUserId] = useState('');

  const canManageOrgUserRoles = useSelector((state: AppState) =>
    hasInternalRole(state, UserDTOInternalRolesEnum.MANAGEORGANIZATIONUSERROLES)
  );

  const [grantOrgUserId, setGrantOrgUserId] = useState('');
  const [revokeOrgUserId, setRevokeOrgUserId] = useState('');
  const orgUsers = useSelector(getOrganizationUsersWithRoles);
  useEffect(() => {
    dispatch(fetchOrganizationUsersRequested(organizationId));
    dispatch(fetchOrganizationUsersWithRolesRequested(organizationId));
  }, [dispatch, organizationId]);

  if (!users || isOrgLoading) return <FullPageLoader />;

  const hasUsers = orgUsers.length > 0;
  const hasAdminRoles = orgUsers.some(({ roles }) => roles.length === 4);
  const requiresAdmin = hasUsers && !hasAdminRoles;

  return (
    <Container>
      {userId ? (
        <NotifyModal
          onDismiss={() => setUserId('')}
          integratorOptions={integratorOptions}
          userId={userId}
          organizationId={organizationId}
        />
      ) : null}
      {grantOrgUserId ? (
        <GrantRolesModal onDismiss={() => setGrantOrgUserId('')} userId={grantOrgUserId} organizationId={organizationId} />
      ) : null}
      {revokeOrgUserId ? (
        <RevokeRolesModal onDismiss={() => setRevokeOrgUserId('')} userId={revokeOrgUserId} organizationId={organizationId} />
      ) : null}
      <Row>
        <Col sm={12}>
          <RepositCard
            title="Organization Users"
            flush
            headerContent={
              requiresAdmin ? (
                <div style={{ marginLeft: 'auto' }}>
                  <NoAdminPill>No Admin Users</NoAdminPill>
                </div>
              ) : null
            }
          >
            <TeamListWrapper>
              {orgUsers.map(({ roles, user: { id, firstName, lastName, email, attributes } }) => {
                const hasAllRoles = roles.length === 4;
                return (
                  <TeamMemberItem key={id}>
                    <TeamMemberItemInner>
                      <TeamMember>
                        <Name>
                          {firstName} {lastName}{' '}
                          <JobTitle>{attributes && attributes.jobTitle ? `(${attributes.jobTitle})` : ''}</JobTitle>
                          {hasAllRoles ? <RolePill>Admin</RolePill> : null}
                        </Name>
                        <Email>{email}</Email>
                      </TeamMember>
                      <div>
                        {canManageOrgUserRoles ? (
                          <div style={{ marginRight: 10, display: 'inline-block' }}>
                            <Button
                              buttonType={hasAllRoles ? 'warning' : 'secondary'}
                              mini="true"
                              onClick={() => {
                                if (hasAllRoles) {
                                  setRevokeOrgUserId(id);
                                } else {
                                  setGrantOrgUserId(id);
                                }
                              }}
                            >
                              {hasAllRoles ? 'Revoke Admin' : 'Grant Admin'}
                            </Button>
                          </div>
                        ) : null}
                        <div style={{ marginRight: 10, display: 'inline-block' }}>
                          <Button buttonType="secondary" mini="true" onClick={() => setUserId(id)}>
                            Notify Integrator
                          </Button>
                        </div>
                        <ViewLink to={`/users/${id}`}>
                          <Button buttonType="secondary" mini="true">
                            View
                          </Button>
                        </ViewLink>
                      </div>
                    </TeamMemberItemInner>
                  </TeamMemberItem>
                );
              })}
            </TeamListWrapper>
          </RepositCard>
        </Col>
      </Row>
    </Container>
  );
};

export default OrganizationUsers;
